import React, { useState, useEffect, useCallback } from 'react';
import './styles/main.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import FacilityManagement from './pages/facilities/FacilityManagement';
import FacilityCreate from './pages/facilities/FacilityCreate';
import FacilityEdit from './pages/facilities/FacilityEdit';
import FacilityView from './pages/facilities/FacilityView';
import RouteManagement from './pages/routes/RouteManagement';
import RouteCreate from './pages/routes/RouteCreate';
import RouteEdit from './pages/routes/RouteEdit';
import RouteView from './pages/routes/RouteView';
import SOSMonitor from './pages/sos/SOSMonitor';
import ModerationDashboard from './pages/moderation/ModerationDashboard';
import ReputationConsole from './pages/reputation/ReputationConsole';
import AuditLogs from './pages/audit/AuditLogs';
import CommercialDashboard from './pages/commercial/CommercialDashboard';
import CatalogManagement from './pages/commercial/CatalogManagement';
import AccountManagement from './pages/commercial/AccountManagement';
import OrderManagement from './pages/commercial/OrderManagement';
import CampaignApprovals from './pages/commercial/CampaignApprovals';
import CouponManagement from './pages/commercial/CouponManagement';

import { API_BASE_URL, getWebSocketUrl } from './config/api';

function AdminDashboard() {
  const { user, logout } = useAuth();
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedFacilityId, setSelectedFacilityId] = useState(null);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [metrics, setMetrics] = useState({
    active_pilgrims: 0,
    groups_count: 0,
    active_sos: 0,
    sync_failures: 0,
    pending_contributions: 0,
    verified_contributions: 0,
    location_updates_per_hour: 0
  });
  const [sosAlerts, setSosAlerts] = useState([]);

  // Safely retrieve auth token under any storage key name
  const getAuthHeaders = useCallback(() => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('admin_token') ||
      localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, []);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [mRes, sRes] = await Promise.all([
        fetch(`${API_BASE_URL}/admin/metrics`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE_URL}/admin/sos-board`, { headers: getAuthHeaders() })
      ]);
      if (mRes.ok) setMetrics(await mRes.json());
      if (sRes.ok) setSosAlerts(await sRes.json());
    } catch (e) {
      console.error('Failed to fetch dashboard data', e);
    }
  }, [getAuthHeaders]);

  // WebSocket Live Operations Setup
  useEffect(() => {
    // 1. Initial REST fetch
    fetchDashboardData();

    // 2. WS Live updates subscription
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('admin_token') ||
      localStorage.getItem('access_token');
    if (!token) return;

    const wsUrl = getWebSocketUrl(`/groups/ws/00000000-0000-0000-0000-000000000000?token=${token}`);
    const socket = new WebSocket(wsUrl);

    socket.onopen = () => {
      console.log('App dashboard operations WS connection opened');
    };

    socket.onmessage = (event) => {
      try {
        const message = JSON.parse(event.data);
        console.log('Live App Dashboard Event:', message);

        // Handle metrics updates from backend broadcast
        if (message.type === 'metrics_update') {
          setMetrics(message.data);
        } else if (message.type === 'sos_alert') {
          // Increment distress alerts count
          setMetrics((prev) => ({
            ...prev,
            active_sos: prev.active_sos + 1
          }));
          // Fetch updated SOS board
          fetch(`${API_BASE_URL}/admin/sos-board`, { headers: getAuthHeaders() })
            .then((res) => res.ok && res.json())
            .then((data) => data && setSosAlerts(data));
        } else if (message.type === 'sos_update') {
          const { status } = message.data;
          if (status === 'resolved' || status === 'cancelled') {
            setMetrics((prev) => ({
              ...prev,
              active_sos: Math.max(0, prev.active_sos - 1)
            }));
          }
          // Fetch updated SOS board
          fetch(`${API_BASE_URL}/admin/sos-board`, { headers: getAuthHeaders() })
            .then((res) => res.ok && res.json())
            .then((data) => data && setSosAlerts(data));
        }
      } catch (err) {
        console.error('Failed to parse websocket message on Dashboard', err);
      }
    };

    return () => {
      socket.close();
    };
  }, [fetchDashboardData, getAuthHeaders]);

  const renderView = () => {
    switch (activeView) {
      case 'dashboard': return <Dashboard metrics={metrics} sosAlerts={sosAlerts} />;
      case 'moderation': return <ModerationDashboard />;
      case 'sos': return <SOSMonitor />;
      case 'reputation': return <ReputationConsole />;
      case 'audit': return <AuditLogs />;
      case 'facilities': return <FacilityManagement onNavigate={setActiveView} onSelectFacility={setSelectedFacilityId} />;
      case 'facility-create': return <FacilityCreate onNavigate={setActiveView} />;
      case 'facility-view': return <FacilityView id={selectedFacilityId} onNavigate={setActiveView} />;
      case 'facility-edit': return <FacilityEdit id={selectedFacilityId} onNavigate={setActiveView} />;
      case 'routes': return <RouteManagement onNavigate={setActiveView} onSelectRoute={setSelectedRouteId} />;
      case 'route-create': return <RouteCreate onNavigate={setActiveView} />;
      case 'route-view': return <RouteView id={selectedRouteId} onNavigate={setActiveView} />;
      case 'route-edit': return <RouteEdit id={selectedRouteId} onNavigate={setActiveView} />;
      case 'journeys': return <JourneyManagement />;
      case 'organizations': return <OrganizationManagement />;
      case 'commercial-dashboard': return <CommercialDashboard onNavigate={setActiveView} />;
      case 'commercial-catalog': return <CatalogManagement />;
      case 'commercial-accounts': return <AccountManagement />;
      case 'commercial-orders': return <OrderManagement />;
      case 'commercial-approvals': return <CampaignApprovals />;
      case 'commercial-coupons': return <CouponManagement />;
      default: return <Dashboard metrics={metrics} sosAlerts={sosAlerts} />;
    }
  };

  return (
    <div className="admin-layout">
      <div className="sidebar">
        <h2 style={{ marginBottom: '24px', color: '#FF5722', fontSize: '20px' }}>Yatra Platform</h2>
        
        <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#888', fontWeight: 'bold', padding: '8px 12px 4px 12px' }}>Operational Health</div>
        <NavItem active={activeView === 'dashboard'} onClick={() => setActiveView('dashboard')} label="Journey Health" />
        <NavItem active={activeView === 'moderation'} onClick={() => setActiveView('moderation')} label="Moderation Queue" />
        <NavItem active={activeView === 'sos'} onClick={() => setActiveView('sos')} label="SOS Ops Center" />
        <NavItem active={activeView === 'reputation'} onClick={() => setActiveView('reputation')} label="Reputation Console" />
        <NavItem active={activeView === 'audit'} onClick={() => setActiveView('audit')} label="Audit Trails" />
        
        <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#FF5722', fontWeight: 'bold', padding: '16px 12px 4px 12px' }}>Commercial Ops</div>
        <NavItem active={activeView === 'commercial-dashboard'} onClick={() => setActiveView('commercial-dashboard')} label="📊 Dashboard" />
        <NavItem active={activeView === 'commercial-catalog'} onClick={() => setActiveView('commercial-catalog')} label="🏷️ Rate Cards & Catalog" />
        <NavItem active={activeView === 'commercial-accounts'} onClick={() => setActiveView('commercial-accounts')} label="🏢 Advertisers" />
        <NavItem active={activeView === 'commercial-orders'} onClick={() => setActiveView('commercial-orders')} label="🛒 Commercial Orders" />
        <NavItem active={activeView === 'commercial-approvals'} onClick={() => setActiveView('commercial-approvals')} label="🛡️ Campaign Approvals" />
        <NavItem active={activeView === 'commercial-coupons'} onClick={() => setActiveView('commercial-coupons')} label="🎟️ Commercial Offers & Coupons" />

        <div style={{ fontSize: '11px', textTransform: 'uppercase', color: '#888', fontWeight: 'bold', padding: '16px 12px 4px 12px' }}>Directory & Routes</div>
        <NavItem active={activeView.startsWith('facility')} onClick={() => setActiveView('facilities')} label="Facility Directory" />
        <NavItem active={activeView.startsWith('route')} onClick={() => setActiveView('routes')} label="Manage Routes" />
        <NavItem active={activeView === 'journeys'} onClick={() => setActiveView('journeys')} label="Journeys" />
        <NavItem active={activeView === 'organizations'} onClick={() => setActiveView('organizations')} label="Partner Orgs" />
      </div>

      <div className="main-content">
        <div className="header">
          <h1>{activeView.charAt(0).toUpperCase() + activeView.slice(1).replace('-', ' ')}</h1>
          <div className="user-profile">
            <span className="user-info">
              {user?.full_name || user?.username || 'Admin'}
              <span className="user-role">{user?.role || ''}</span>
            </span>
            <button className="btn-logout" onClick={logout}>Logout</button>
          </div>
        </div>
        {renderView()}
      </div>
    </div>
  );
}

function NavItem({ active, onClick, label }) {
  return (
    <div className={`nav-item ${active ? 'active' : ''}`} onClick={onClick}>
      {label}
    </div>
  );
}

function Dashboard({ metrics, sosAlerts }) {
  return (
    <>
      <div className="stats-grid">
        <StatCard label="Online Users" value={metrics.active_pilgrims} />
        <StatCard label="Active Groups" value={metrics.groups_count} />
        <StatCard label="Open SOS" value={metrics.active_sos} color={metrics.active_sos > 0 ? 'red' : null} />
        <StatCard label="Sync Failures" value={metrics.sync_failures} color={metrics.sync_failures > 0 ? 'red' : null} />
        <StatCard label="Pending Contributions" value={metrics.pending_contributions || 0} />
        <StatCard label="Verified Contributions" value={metrics.verified_contributions || 0} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
        <div className="card">
          <h3>Recent Distress SOS Alarms</h3>
          {!Array.isArray(sosAlerts) || sosAlerts.length === 0 ? (
            <p style={{ marginTop: '16px', color: 'green', fontWeight: 'bold' }}>
              ✅ All clear! No active distress calls.
            </p>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Severity</th>
                  <th>Status</th>
                  <th>Time</th>
                </tr>
              </thead>
              <tbody>
                {sosAlerts.map((alert) => (
                  <tr key={alert.id} style={{ fontWeight: alert.severity === 'CRITICAL' ? 'bold' : 'normal' }}>
                    <td>{alert.user_name}</td>
                    <td>
                      <span className={`badge-${alert.severity === 'CRITICAL' || alert.severity === 'HIGH' ? 'danger' : 'info'}`}>
                        {alert.severity}
                      </span>
                    </td>
                    <td><span className="badge-danger">{alert.status?.toUpperCase()}</span></td>
                    <td>{new Date(alert.created_at).toLocaleTimeString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="card">
          <h3>System Scalability Summary</h3>
          <ul style={{ listStyleType: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '16px' }}>
            <li><strong>Monthly Partitioning Strategy:</strong> active on PostgreSQL for location data.</li>
            <li><strong>Module 5G-01 Foundation Status:</strong> <span style={{ color: '#059669', fontWeight: 'bold' }}>OPERATIONAL</span> (PILOT_5 Active, PILOT_25 Disabled)</li>
            <li><strong>Active Location Pings (Last Hour):</strong> {metrics.location_updates_per_hour} pings</li>
            <li><strong>Realtime Socket Engine:</strong> Active via Redis Pub/Sub</li>
            <li><strong>Presence Keep-alive:</strong> 60s heartbeats</li>
          </ul>
        </div>
      </div>
    </>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div className="card stat-card">
      <h3>{label}</h3>
      <div className="value" style={{ color: color }}>{value}</div>
    </div>
  );
}

function JourneyManagement() {
  const [journeys, setJourneys] = useState([]);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('admin_token') ||
      localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    const fetchJourneys = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/journeys?active_only=false`, {
          headers: getAuthHeaders()
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.detail || 'Failed to fetch journeys');
          setJourneys([]);
          return;
        }
        setJourneys(Array.isArray(data) ? data : data.data || []);
        setError(null);
      } catch (e) {
        setError('Network error or server unreachable');
        setJourneys([]);
      }
    };
    fetchJourneys();
  }, []);

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h3>Active & Planned Journeys</h3>
        <button className="btn-primary">+ New Journey</button>
      </div>

      {error && (
        <div style={{ color: '#f44336', marginBottom: '15px', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
          Error: {error}
        </div>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Status</th>
            <th>Start Date</th>
          </tr>
        </thead>
        <tbody>
          {journeys.map((j) => (
            <tr key={j.id}>
              <td>{j.name}</td>
              <td>{j.journey_type}</td>
              <td>
                <span className={`badge-${j.status === 'ACTIVE' ? 'success' : 'info'}`}>
                  {j.status}
                </span>
              </td>
              <td>{j.start_date ? new Date(j.start_date).toLocaleDateString() : 'N/A'}</td>
            </tr>
          ))}
          {journeys.length === 0 && !error && (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                No journeys found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function OrganizationManagement() {
  const [orgs, setOrgs] = useState([]);
  const [error, setError] = useState(null);

  const getAuthHeaders = () => {
    const token =
      localStorage.getItem('token') ||
      localStorage.getItem('admin_token') ||
      localStorage.getItem('access_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    const fetchOrgs = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/organizations/`, {
          headers: getAuthHeaders()
        });
        const data = await res.json();
        if (!res.ok) {
          setError(data.detail || 'Failed to fetch organizations');
          setOrgs([]);
          return;
        }
        setOrgs(Array.isArray(data) ? data : []);
        setError(null);
      } catch (e) {
        setError('Network error');
        setOrgs([]);
      }
    };
    fetchOrgs();
  }, []);

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <h3>Partner Organizations</h3>
        <button className="btn-primary">+ Register NGO/Org</button>
      </div>

      {error && (
        <div style={{ color: '#f44336', marginBottom: '15px', padding: '10px', backgroundColor: '#ffebee', borderRadius: '4px' }}>
          Error: {error}
        </div>
      )}

      <table className="admin-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Type</th>
            <th>Contact Person</th>
            <th>Phone</th>
          </tr>
        </thead>
        <tbody>
          {orgs.map((o) => (
            <tr key={o.id}>
              <td>{o.name}</td>
              <td>{o.organization_type}</td>
              <td>{o.contact_person}</td>
              <td>{o.contact_number}</td>
            </tr>
          ))}
          {orgs.length === 0 && !error && (
            <tr>
              <td colSpan="4" style={{ textAlign: 'center', padding: '20px' }}>
                No organizations found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <ProtectedRoute>
        <AdminDashboard />
      </ProtectedRoute>
    </AuthProvider>
  );
}

export default App;